import React from 'react';
import { SparkDialog } from 'sharedComponents/UI';
import FileUploadStep from './FileUploadStep';
import CreateMemoryForm from './CreateMemoryForm';
import { MemoriesIcon } from 'components/SVG';
import { Box, Typography } from '@mui/material';
import { useBlobStorage } from 'hooks/useBlobStorage';
import { useWorkspaceContext } from 'hooks/common/useWorkspaceContext';
import { Row } from 'components/Settings/workspace/SingleWorkSpace/KnowledgeTab';
import { useFileUploads } from 'hooks/useFileUploads';
import { useAddMemory } from 'hooks/memory';
import { useAddMemoryCategory } from 'hooks/Categories';
import { useStatusBarStore } from 'stores';
import { FileRejection } from 'react-dropzone';

const STEP_NAMES = {
  UPLOAD_FILES: 'uploadFiles',
  ADD_MEMORY: 'addMemory',
};

type CreateMemoryModalProps = {
  open: boolean;
  onClose: () => void;
  selectedMemoryData?: Row | null;
  onMemoryAdd?: (id: string) => void;
};
function CreateMemoryModal({
  open,
  onClose,
  onMemoryAdd,
  selectedMemoryData,
}: CreateMemoryModalProps) {
  const { getWorkspace } = useWorkspaceContext();
  const setStatusConfig = useStatusBarStore((state) => state.setStatusConfig);
  const workSpace = getWorkspace();
  const { handleFileUpload, handleDelete, uploads, setUploads } =
    useBlobStorage(workSpace?.attributes.containerName || 'temp');
  const { mutateAsync: addMemory } = useAddMemory();
  const { mutateAsync: addMemoryCategory } = useAddMemoryCategory();
  const [currentStep, setCurrentStep] = React.useState<string>(
    STEP_NAMES.UPLOAD_FILES
  );
  const { mutateAsync: uploadFiles } = useFileUploads();

  const uploadFilesURLs = React.useMemo(
    () =>
      uploads
        .filter((upload) => upload.isSuccessful)
        .map((upload) => upload.url),
    [uploads]
  );
  const handleNextClick = () => {
    setCurrentStep(STEP_NAMES.ADD_MEMORY);
  };
  const handlePreviousClick = () => {
    setCurrentStep(STEP_NAMES.UPLOAD_FILES);
  };
  const handleModalClose = () => {
    setUploads([]);
    setCurrentStep(STEP_NAMES.UPLOAD_FILES);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedMemoryData) {
      return;
    }
    const { id, name, imageURL, description, categoryID } = selectedMemoryData;
    const memoryResponse = await addMemory({
      name,
      logoURL: imageURL,
      description,
      recommendedMemoryID: Number(id),
    });
    if (categoryID) {
      const categoryPromises = addMemoryCategory({
        categoryID: categoryID,
        memoryID: memoryResponse.data.id,
      });
      await Promise.all([categoryPromises]);
    }
    await uploadFiles({
      memoryID: memoryResponse.data.id,
      files: uploadFilesURLs.map((fileUrl) => ({ url: fileUrl })),
    });
    setStatusConfig({
      statusBarType: 'memoryCreation',
      statusBarID: memoryResponse.data.id,
    });
    onClose();
  };
  const handleSuccessfulUpload = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    const result = await handleFileUpload(acceptedFiles, fileRejections);
    const URLs = result?.map((upload) => upload.url);

    return URLs;
  };

  const renderMemoryDialogTitle = () => {
    return (
      <Box display='flex' alignItems='center' gap={2}>
        <Box
          bgcolor='white'
          width='2rem'
          borderRadius='10px'
          sx={{ aspectRatio: 1 }}
          display='flex'
          justifyContent='center'
          alignItems='center'
        >
          <MemoriesIcon />
        </Box>
        <Typography
          variant='h5'
          sx={{
            fontFamily: 'Mont-semibold',
          }}
        >
          Add Knowledge Source
        </Typography>
      </Box>
    );
  };

  return (
    <SparkDialog
      title={renderMemoryDialogTitle()}
      open={open}
      onClose={handleModalClose}
    >
      {currentStep === STEP_NAMES.UPLOAD_FILES && (
        <FileUploadStep
          handleNextClick={selectedMemoryData ? handleSubmit : handleNextClick}
          handleFileUpload={handleSuccessfulUpload}
          uploads={uploads}
          handleDelete={handleDelete}
          shouldSaveButtonRender={selectedMemoryData ? true : false}
        />
      )}
      {currentStep === STEP_NAMES.ADD_MEMORY && (
        <CreateMemoryForm
          handlePreviousClick={handlePreviousClick}
          onClose={onClose}
          onMemoryAdd={onMemoryAdd}
          uploadFilesURLs={uploadFilesURLs.filter(
            (url): url is string => !!url
          )}
        />
      )}
    </SparkDialog>
  );
}

export default CreateMemoryModal;
